import type { PageNode } from "../../../shared/src/pages";
import { pageUpdateDate, type PageUpdate } from "../../../shared/src/page-updates-runtime";
import { canvasPlaceholderSetting } from "../../../shared/src/canvas-placeholders";
import { CanvasImage } from "./canvas-image";
import { PostExampleComments } from "./post-example-comments";

/** A post's discovery picture, or the canvas's stand-in for an example's empty one. */
export function PostCover({
  item,
  href,
  canvas,
}: {
  item: PageUpdate;
  href: string;
  canvas: boolean;
}) {
  const standIn = canvasPlaceholderSetting(item.image);
  if (standIn !== null)
    return canvas ? (
      <CanvasImage
        attributes={{
          className: "drate-post-cover",
          style: { "--page-node-aspect": "16 / 9" },
        }}
        setting={standIn}
      />
    ) : null;
  return item.image ? (
    <a className="drate-post-cover" href={href} tabIndex={-1} aria-hidden="true">
      <img src={item.image} alt="" loading="lazy" decoding="async" width="1200" height="675" />
    </a>
  ) : null;
}

/** Only the access-checked feed projection reaches this presentation. */
export function PostFeedCard({
  item,
  node,
  creatorName,
  editHref,
  linkTo = (href) => href,
  canvas = false,
}: {
  item: PageUpdate;
  node: Extract<PageNode, { kind: "updates" }>;
  creatorName?: string;
  editHref?: string;
  linkTo?: (href: string) => string;
  canvas?: boolean;
}) {
  const name = creatorName || "Creator";
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("");
  const href = item.url ? linkTo(item.url) : null;
  return (
    <article
      className="drate-post-card"
      data-update-id={item.id}
      data-locked={item.locked}
      data-post-density={node.postDensity ?? "comfortable"}
    >
      <header className="drate-post-byline">
        <span className="drate-post-avatar" aria-hidden="true">
          {initials}
        </span>
        <div>
          <strong>{name}</strong>
          {node.dateFormat !== "hidden" && item.date && (
            <time dateTime={item.date}>{pageUpdateDate(item.date, node)}</time>
          )}
        </div>
        <span className="drate-post-audience">
          {item.locked || item.access === "members" ? "Members only" : "Public"}
        </span>
      </header>
      <PostCover item={item} href={href ?? linkTo("/join")} canvas={canvas} />
      <div className="drate-post-content">
        <h3>{href ? <a href={href}>{item.title}</a> : item.title}</h3>
        {item.locked ? (
          <>
            {item.summary && <p className="drate-post-summary">{item.summary}</p>}
            <div className="drate-post-gate">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <rect x="5" y="10" width="14" height="11" rx="3" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
              </svg>
              <strong>A little more, just for members.</strong>
              <p>Become a member to unlock this post and support the work.</p>
              <a className="button button-primary" href={linkTo("/join")}>
                Unlock with membership
              </a>
              <span>
                Already a member? <a href={linkTo("/sign-in")}>Sign in</a>
              </span>
            </div>
          </>
        ) : item.story ? (
          <div className="drate-post-body story" dangerouslySetInnerHTML={{ __html: item.story }} />
        ) : (
          <p className="drate-post-body">{item.body || item.summary}</p>
        )}
      </div>
      <PostExampleComments
        item={item}
        creatorName={name}
        linkTo={linkTo}
        defaultOpen={node.postDensity === "compact" ? false : undefined}
      />
      {href && (
        <footer className="drate-post-footer">
          <a href={href}>
            {item.locked ? "View membership details" : "Open post"}
            <span aria-hidden="true">↗</span>
          </a>
          {editHref && (
            <a href={editHref} data-reload="">
              Edit post
            </a>
          )}
        </footer>
      )}
    </article>
  );
}
